import {
  FilterLegacy,
  InputFieldConfig,
  SimpleEventFilterType,
  SimpleEventType,
  type ActiveCohortBuilderItemLegacy,
} from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import { buildConfigMap } from '../utils/buildConfigMap';
import { FILTER_KEYS, SIMPLE_EVENT_FILTER_MAPPING } from '../utils/filterTypes';
import { getActiveFilter } from '../utils/getActiveFilter';
import { mutateInputFields } from '../utils/inputFieldUtils';

// Define types for filter keys and input fields which are fields in the ui dsl

function resetInputFieldValuesIfOptionsChanged(
  filter: FilterLegacy,
  configMap: Record<string, InputFieldConfig>
) {
  filter.inputFields.forEach((inputField) => {
    const newConfig = configMap[inputField.id];
    const newOptions = newConfig?.options?.map((o) => o.value) || [];

    if (inputField.value && newOptions.length > 0) {
      const currentValues = inputField.value.split(',');
      const allValuesInNewOptions = currentValues.every((v) => newOptions.includes(v));
      if (!allValuesInNewOptions) {
        inputField.value = '';
      }
    }
  });
}

export const addSimpleEvent = (
  simpleEvent: ActiveCohortBuilderItemLegacy,
  conceptGroups: ConceptGroup[]
): ActiveCohortBuilderItemLegacy => {
  // the simple event can have multiple filters. Get the one the user is working on.
  const { filter, type: filterType } = getActiveFilter(simpleEvent);

  if (!filter) {
    // This is an edit action; build the configMap for all filters
    const allConfigMaps = FILTER_KEYS.reduce((acc, key) => {
      const filters = simpleEvent[key] as FilterLegacy[];
      const filterType = SIMPLE_EVENT_FILTER_MAPPING[key];
      filters.forEach((f) => {
        const configMap = buildConfigMap(
          simpleEvent.type as SimpleEventType,
          f,
          filterType,
          conceptGroups,
          true // edit mode
        );
        Object.assign(acc, configMap);
      });
      return acc;
    }, {} as Record<string, InputFieldConfig>);
    return {
      ...simpleEvent,
      inputFieldConfigMap: {
        ...simpleEvent.inputFieldConfigMap,
        ...allConfigMaps,
      },
    };
  }

  // Generate the input fields for the filter
  mutateInputFields(filter, filterType);

  // Generate config map which contains the allowed options
  // and input types (dropdown, combobox, integer, float, ...)
  // for each filter input
  const configMap = buildConfigMap(
    simpleEvent.type as SimpleEventType,
    filter,
    filterType as SimpleEventFilterType,
    conceptGroups
  );

  // reset the input field filter value if the current value is not in the available options
  resetInputFieldValuesIfOptionsChanged(filter, configMap);

  return {
    ...simpleEvent,
    inputFieldConfigMap: {
      ...simpleEvent.inputFieldConfigMap,
      ...configMap,
    },
  };
};
